// src/composables/useRingCentral.ts
// RingCentral OAuth Integration Service - Vue Composable API
// Provides easy-to-use reactive interface for RingCentral integration features
// RELEVANT FILES: src/stores/ringcentral.ts, src/types/ringcentral.ts, src/api/ringcentral-api.ts

import { storeToRefs } from 'pinia';
import { computed } from 'vue';

// OAuth config removed - now using predefined settings
import { useRingCentralStore } from '@/stores/ringcentral';

/**
 * Main RingCentral integration composable
 * Provides reactive state and actions for RingCentral OAuth integration
 *
 * @example
 * ```typescript
 * const {
 *   isConnected,
 *   currentConnection,
 *   loading,
 *   error,
 *   initiateOAuth,
 *   disconnect
 * } = useRingCentral()
 *
 * // Check if current org is connected
 * if (isConnected.value) {
 *   console.log('RingCentral connected!')
 * }
 *
 * // Connect RingCentral
 * await initiateOAuth('org-123')
 * ```
 */
export function useRingCentral() {
  const store = useRingCentralStore();

  // Reactive state from store
  const {
    organization,
    organizations,
    connections,
    oauthState,
    loading,
    error,
    lastError,
    currentConnection,
    isConnected,
    canManageConnection,
    userConnections,
    connectionSummary,
  } = storeToRefs(store);

  // Store actions
  const {
    createOrganization,
    getOrganization,
    getUserOrganizations,
    initiateOAuth,
    checkOAuthStatus,
    loadConnection,
    refreshConnection,
    disconnect,
    testConnection,
    loadAccountInfo,
    getAccountInfo,
    clearError,
    reset,
    initialize,
  } = store;

  return {
    // ============ Reactive State ============
    organization,
    organizations,
    connections,
    oauthState,
    loading,
    error,
    lastError,

    // ============ Computed State ============
    currentConnection,
    isConnected,
    canManageConnection,
    userConnections,
    connectionSummary,

    // ============ Organization Actions ============
    createOrganization,
    getOrganization,
    getUserOrganizations,

    // ============ OAuth Actions ============
    initiateOAuth,
    checkOAuthStatus,

    // ============ Connection Actions ============
    loadConnection,
    refreshConnection,
    disconnect,
    testConnection,

    // ============ Account Actions ============
    loadAccountInfo,
    getAccountInfo,

    // ============ Utility Actions ============
    clearError,
    reset,
    initialize,
  };
}

/**
 * Organization-specific RingCentral composable
 * Provides RingCentral functionality scoped to a specific organization
 *
 * @param orgId - Organization ID to scope the functionality to
 *
 * @example
 * ```typescript
 * const orgRingCentral = useRingCentralForOrg('org-123')
 *
 * // Get connection status for specific org
 * const isOrgConnected = orgRingCentral.isConnected.value
 *
 * // Connect specific org
 * await orgRingCentral.connect()
 *
 * // Disconnect specific org
 * await orgRingCentral.disconnect()
 * ```
 */
export function useRingCentralForOrg(orgId: string) {
  const store = useRingCentralStore();
  const { connections, loading, error } = storeToRefs(store);

  // Organization-specific computed properties
  const connection = computed(() => connections.value[orgId] || null);
  const isConnected = computed(() => connection.value?.status === 'connected');
  const isPending = computed(() => connection.value?.status === 'pending');
  const hasError = computed(() => connection.value?.status === 'error');

  // Organization-specific actions
  const connect = async () => {
    return store.initiateOAuth(orgId);
  };

  const disconnect = async () => {
    return store.disconnect(orgId);
  };

  const refresh = async () => {
    return store.refreshConnection(orgId);
  };

  const test = async () => {
    return store.testConnection(orgId);
  };

  const loadConnection = async (force = false) => {
    return store.loadConnection(orgId, force);
  };

  const getAccountInfo = () => {
    if (!connection.value) return null;
    return store.getAccountInfo(connection.value.connectionId);
  };

  return {
    // State
    connection,
    isConnected,
    isPending,
    hasError,
    loading,
    error,

    // Actions
    connect,
    disconnect,
    refresh,
    test,
    loadConnection,
    getAccountInfo,
  };
}

/**
 * RingCentral connection status composable
 * Provides detailed connection status and health information
 *
 * @example
 * ```typescript
 * const status = useRingCentralStatus()
 *
 * // Get overall status
 * console.log('Total connections:', status.summary.value.total)
 * console.log('Connected:', status.summary.value.connected)
 *
 * // Check if any connections have errors
 * if (status.hasErrors.value) {
 *   console.log('Some connections have errors')
 * }
 * ```
 */
export function useRingCentralStatus() {
  const store = useRingCentralStore();
  const { userConnections, connectionSummary } = storeToRefs(store);

  const hasConnections = computed(() => userConnections.value.length > 0);
  const hasConnectedAccounts = computed(() => connectionSummary.value.connected > 0);
  const hasPendingConnections = computed(() => connectionSummary.value.pending > 0);
  const hasErrors = computed(() => connectionSummary.value.error > 0);

  const allConnected = computed(
    () =>
      hasConnections.value && connectionSummary.value.connected === connectionSummary.value.total,
  );

  const connectionHealth = computed(() => {
    if (!hasConnections.value) return 'none';
    if (allConnected.value) return 'healthy';
    if (hasErrors.value) return 'error';
    if (hasPendingConnections.value) return 'pending';
    return 'partial';
  });

  return {
    // Connection counts
    summary: connectionSummary,
    connections: userConnections,

    // Status flags
    hasConnections,
    hasConnectedAccounts,
    hasPendingConnections,
    hasErrors,
    allConnected,

    // Overall health
    connectionHealth,
  };
}

/**
 * RingCentral OAuth flow composable
 * Handles the OAuth authorization flow with state management
 *
 * @example
 * ```typescript
 * const oauth = useRingCentralOAuth()
 *
 * // Start OAuth flow
 * await oauth.startFlow('org-123', {
 *   scopes: ['ReadAccounts', 'ReadCallLog']
 * })
 *
 * // Handle callback (usually in callback page)
 * await oauth.handleCallback(code, state)
 *
 * // Check OAuth state
 * if (oauth.isInProgress.value) {
 *   console.log('OAuth flow in progress...')
 * }
 * ```
 */
export function useRingCentralOAuth() {
  const store = useRingCentralStore();
  const { oauthState, loading, error } = storeToRefs(store);

  const isInProgress = computed(() => !!oauthState.value);

  const startFlow = async (orgId: string) => {
    return store.initiateOAuth(orgId);
  };

  const checkStatus = async (stateId: string) => {
    return store.checkOAuthStatus(stateId);
  };

  const cancelFlow = () => {
    // Clean up OAuth session
    sessionStorage.removeItem('ringcentral_oauth_state');
    sessionStorage.removeItem('ringcentral_oauth_orgId');
    store.oauthState = null;
  };

  return {
    // State
    oauthState,
    isInProgress,
    loading,
    error,

    // Actions
    startFlow,
    checkStatus,
    cancelFlow,
  };
}

/**
 * RingCentral account information composable
 * Provides access to RingCentral account details and extensions
 *
 * @example
 * ```typescript
 * const account = useRingCentralAccount()
 *
 * // Get current org's account info
 * const accountInfo = account.currentAccountInfo.value
 *
 * if (accountInfo) {
 *   console.log('Account:', accountInfo.name)
 *   console.log('Main number:', accountInfo.mainNumber)
 * }
 *
 * // Load account info for connection
 * await account.loadAccountInfo('connection-123')
 * ```
 */
export function useRingCentralAccount() {
  const store = useRingCentralStore();
  const { currentConnection } = storeToRefs(store);

  const currentAccountInfo = computed(() => {
    if (!currentConnection.value) return null;
    return store.getAccountInfo(currentConnection.value.connectionId);
  });

  const hasAccountInfo = computed(() => !!currentAccountInfo.value);

  const loadAccountInfo = async (connectionId: string) => {
    return store.loadAccountInfo(connectionId);
  };

  const getAccountInfo = (connectionId: string) => {
    return store.getAccountInfo(connectionId);
  };

  return {
    // Current account
    currentAccountInfo,
    hasAccountInfo,

    // Actions
    loadAccountInfo,
    getAccountInfo,
  };
}

/**
 * RingCentral error handling composable
 * Provides error state management and formatting utilities
 *
 * @example
 * ```typescript
 * const errorHandler = useRingCentralError()
 *
 * // Check for errors
 * if (errorHandler.hasError.value) {
 *   console.log('Error:', errorHandler.formattedError.value)
 * }
 *
 * // Clear errors
 * errorHandler.clearError()
 *
 * // Handle specific error
 * try {
 *   await someRingCentralOperation()
 * } catch (err) {
 *   errorHandler.handleError(err)
 * }
 * ```
 */
export function useRingCentralError() {
  const store = useRingCentralStore();
  const { error, lastError } = storeToRefs(store);

  const hasError = computed(() => !!error.value || !!lastError.value);

  const formattedError = computed(() => {
    if (error.value) return error.value;
    if (lastError.value) return lastError.value.message;
    return null;
  });

  const handleError = (err: any) => {
    console.error('RingCentral operation failed:', err);
    // The store will handle error formatting
  };

  const clearError = () => {
    store.clearError();
  };

  return {
    // State
    error,
    lastError,
    hasError,
    formattedError,

    // Actions
    handleError,
    clearError,
  };
}

/**
 * Default export for main composable
 */
export default useRingCentral;
